import { Spinner } from '@ibsys/kit';
import { FC, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  asChartRuleError,
  useCustomChartData,
  type CustomChartDataParams,
  type CustomChartDefinition,
  type IndicatorSelectHandler,
} from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { pickChartAppearance } from '../../model/dashboardPresetForm';
import { chartBindingDataParams, type CustomChartBindingsState } from '../../model/useCustomChartBindings';
import { ChartCardHeaderProvider } from './ChartCardHeaderContext';
import { ChartCardShell } from './ChartCardShell';
import type { ChartAppearance } from './chartElements';
import { ChartSliceSwitcher } from './ChartSliceSwitcher';
import { CustomChartView } from './CustomChartView';

/** Фильтры панели дашборда, общие для всех диаграмм пресета. */
export type CustomChartPanelFilters = Omit<
  CustomChartDataParams,
  'chartType' | 'rule' | 'signature' | 'bindings' | 'bindingOptionQueries'
>;

type Props = {
  chart: CustomChartDefinition;
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: по ним окрашиваются элементы со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Разрезы и выбранное; не заданы — диаграмма наполняется одними фильтрами панели */
  bindings?: CustomChartBindingsState;
  /** Переход к диалогам выбранного элемента; не задан — содержимое переходов не выполняет */
  onSelect?: IndicatorSelectHandler;
  /** Открытие одного диалога; не задано — содержимое переходов не выполняет */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Диаграмма пользовательского дашборда: наполняется по своему правилу, общим фильтрам панели и
 * значениям своих мест подстановки — выбранному разрезу и выбранному в ведущей диаграмме.
 *
 * Описание при показе заново не истолковывается — правило закреплено за диаграммой ещё в её
 * свойствах. Отказ по правилу показывается в самом блоке: остальные диаграммы дашборда
 * наполняются как обычно.
 *
 * Диаграмма, записанная прежним составом правила (без запроса), наполнению не подлежит: её
 * нужно пересобрать. Об этом сказано в её блоке, и к бэкенду за ней не ходят — отказ был бы
 * тем же самым, только неявным.
 *
 * Связанная диаграмма до выбора в ведущей показывает подсказку выбрать, а не пустоту: пустой блок
 * читался бы как «данных нет», хотя их просто не о чем спрашивать.
 *
 * Переходы к диалогам бывают погашены — карточка получает их незаданными и передаёт содержимому
 * как есть: тогда оно и не переходит, и нажимаемым не показано.
 *
 * Пока диаграмма, уже показавшая данные, получает новые — правлен запрос, сменились тип или фильтры
 * панели, — прежнее содержимое остаётся на месте под затемнением с индикатором загрузки и переходов
 * не выполняет: оно уже не отвечает тому, что запрошено. Перерисовка без запроса — размер,
 * оформление — затемнения не даёт: нового ключа кэша у неё нет.
 *
 * Перерисовывается диаграмма только сменой своих данных и своего состава: правка соседней диаграммы
 * её содержимое не трогает — и не пересчитывает его, пока соседку переносят или растягивают.
 */
export const CustomChartCard: FC<Props> = memo(
  ({ chart, filters, zoneRanges, bindings, onSelect, onOpenConversation }) => {
    const { t } = useTranslation('qualityDashboard');
    // Оформление идёт мимо наполнения: смена цветов, направления и порядка перерисовывает уже полученные
    // данные, не давая нового ключа кэша. Собрано один раз на состав диаграммы — представлению оно
    // достаётся тем же значением, пока состав не правили.
    const appearance = useMemo(
      (): ChartAppearance => ({
        ...pickChartAppearance(chart),
        labelWidth: chart.labelWidth,
        tableView: chart.tableView,
      }),
      [chart],
    );
    const isRuleOutdated = !chart.rule?.query;
    const bindingValues = bindings ? bindings.values : {};
    const isAwaitingSelection = bindingValues === null;

    const { data, isLoading, isFetching, isPlaceholderData, error } = useCustomChartData(
      isRuleOutdated || bindingValues === null
        ? null
        : {
            chartType: chart.chartType,
            rule: chart.rule,
            signature: chart.signature || null,
            ...filters,
            ...chartBindingDataParams(chart, bindingValues),
          },
    );

    const sliceSwitcher = bindings?.slice ? (
      <ChartSliceSwitcher
        binding={bindings.slice.binding}
        // Значения разреза, которых описание не перечислило, отбирает наполнение — то самое,
        // которым нарисована эта диаграмма: переключатель показывает её же разрез.
        options={bindings.slice.binding.options ?? data?.bindingOptions?.[bindings.slice.binding.name]}
        value={bindings.slice.value}
        onChange={bindings.slice.onChange}
      />
    ) : null;

    const shell = (content: ReactNode) => (
      <ChartCardHeaderProvider action={sliceSwitcher}>
        <ChartCardShell title={chart.title}>{content}</ChartCardShell>
      </ChartCardHeaderProvider>
    );

    if (isRuleOutdated) {
      return shell(<p className="text-sm text-muted-foreground">{t('presets.chart.outdatedRule')}</p>);
    }

    if (isAwaitingSelection) {
      return shell(<p className="text-sm text-muted-foreground">{t('presets.chart.awaitingSelection')}</p>);
    }

    if (isLoading) {
      return shell(
        <div className="flex justify-center py-8">
          <Spinner className="size-6" />
        </div>,
      );
    }

    if (error) {
      const ruleError = asChartRuleError(error);
      return shell(<p className="text-sm text-destructive">{ruleError?.message || t('presets.chart.loadFailed')}</p>);
    }

    if (!data || data.total === 0) {
      return shell(<EmptyState />);
    }

    const isRefetching = isFetching && isPlaceholderData;

    return (
      <ChartCardHeaderProvider action={sliceSwitcher}>
        <div className="relative grid min-h-0 min-w-0">
          <CustomChartView
            chartId={chart.id}
            title={chart.title}
            data={data}
            zoneRanges={zoneRanges}
            appearance={appearance}
            hasFixedHeight={chart.height !== undefined}
            onSelect={isRefetching ? undefined : onSelect}
            onOpenConversation={isRefetching ? undefined : onOpenConversation}
            onSelectElement={isRefetching ? undefined : bindings?.onSelectElement}
            selectedElement={bindings?.selectedElement}
          />
          {/* Затемнение серым, а не светлой вуалью: прежнее содержимое под ним видно приглушённым */}
          {isRefetching && (
            <div className="absolute inset-0 z-10 grid place-items-center rounded-xl bg-muted-foreground/40">
              <Spinner className="size-6" />
            </div>
          )}
        </div>
      </ChartCardHeaderProvider>
    );
  },
);
